import React, { FC, useEffect } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';
import { toast } from 'react-toastify';

import { TRoleListing } from '~types/feat/admin';
import { TDropdownOption } from '~types/components/labelledDropdown';

import RolePermissions from '../RolesRolePermissions';
import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';
import LabelledDropdown from '~components/atoms/LabelledDropdown';

import { ModuleEnum, RoleStatusEnum } from '~utils/enums';
import UtilFunctions from '~utils/functions';

import * as FormStyles from './_rolesCreateRoleForm.module.scss';
import { ModulePermissions } from '~utils/constants';

const RolesRoleDetailsFormValidation = Yup.object().shape({
    roleName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().matchNumbersAndAlphabets().required('Role Name is required'),
});

type TRolesRoleDetailsForm = {
    submitButtonLabel: string;
    onSubmit: (data: TRoleListing) => void;
    error: string;
    isEditing?: boolean;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
    initialRoleDetails?: TRoleListing;
    setError?: (errorString: string) => void;
}

const RolesRoleDetailsForm: FC<TRolesRoleDetailsForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    isEditing,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
    initialRoleDetails,
    setError,
}) => {
    const { Misc: { blockEnterKey } } = UtilFunctions;

    const RolesRoleDetailsFormInitialValues = {
        roleName: initialRoleDetails?.roleName || '',
        access: initialRoleDetails?.access || [],
        status: initialRoleDetails?.status ? RoleStatusEnum[initialRoleDetails?.status] as keyof typeof RoleStatusEnum : '',
        roleId: initialRoleDetails?.roleId || 0,
        createdAt: initialRoleDetails?.createdAt || '',
        updatedAt: initialRoleDetails?.updatedAt || '',
        allBranchAccess: initialRoleDetails?.allBranchAccess || false,
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: RolesRoleDetailsFormInitialValues,
        validationSchema: RolesRoleDetailsFormValidation,
        onSubmit: (values) => {
            const { status, ...restOfValues } = values;

            onSubmit({
                ...restOfValues,
                status: RoleStatusEnum[status as keyof typeof RoleStatusEnum],
            });
        },
    });

    useEffect(() => {
        if (!isEditing) {
            formik.resetForm();
        }
    }, [isEditing]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        if (!formik.values.roleName.length && setError) {
            setError('Role Name is required!');
        }

        formik.handleSubmit(e);
    };

    const statusOptions = Object.values(RoleStatusEnum).filter(item => Number.isNaN(Number(item))).map(item => {
        return {
            label: item as string,
            value: RoleStatusEnum[item as keyof typeof RoleStatusEnum],
        };
    });

    const handleOnSelectStatus = (option: TDropdownOption) => {
        formik.setValues({
            ...formik.values,
            status: option.label,
        });
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (error && setError) setError('');

        // Reset the form if the status is changed to not deactivated and changed back to deactivated (not allowed to update details if status is deactivated)
        if (e.target.value === RoleStatusEnum[RoleStatusEnum.Deactivated]
            && initialRoleDetails?.status === RoleStatusEnum.Deactivated
            && formik.values.status !== RoleStatusEnum[RoleStatusEnum.Deactivated]
        ) {
            toast.info('Form is reset');
            formik.resetForm();
        }

        formik.setFieldValue('status', e.target.value);
    };

    // Toggling "All Branches Access" gates Market Status:
    // - turning it ON: pre-tick Market Status's permissions into access, then the user may freely
    //   toggle them further (RolesRolePermissions unlocks the checkboxes once allBranchAccess is true)
    // - turning it OFF: strip Market Status's permissions back out of access and re-lock the checkboxes
    const handleAllBranchAccessChange = () => {
        const newValue = !formik.values.allBranchAccess;

        formik.setFieldValue('allBranchAccess', newValue);

        const marketStatusPermissions = ModulePermissions[ModuleEnum.MarketStatus] || [];

        if (newValue) {
            const newAccess = marketStatusPermissions.reduce((acc, permission) => {
                const existingAccessIndex = acc.findIndex(access => access.permission === permission);

                if (existingAccessIndex >= 0) {
                    const existingModules = acc[existingAccessIndex].modules;

                    if (existingModules.includes(ModuleEnum.MarketStatus)) {
                        return acc;
                    }

                    const updatedEntry = {
                        ...acc[existingAccessIndex],
                        modules: [...existingModules, ModuleEnum.MarketStatus],
                    };

                    return [
                        ...acc.slice(0, existingAccessIndex),
                        updatedEntry,
                        ...acc.slice(existingAccessIndex + 1),
                    ];
                }

                return [
                    ...acc,
                    { permission, modules: [ModuleEnum.MarketStatus] },
                ];
            }, formik.values.access);

            formik.setFieldValue('access', newAccess);
        } else {
            const newAccess = formik.values.access.map(access => ({
                ...access,
                modules: access.modules.filter(module => module !== ModuleEnum.MarketStatus),
            }));

            formik.setFieldValue('access', newAccess);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <div className={FormStyles['form-subcontainer']}>
                    <LabelledInput
                        id='roleId'
                        name='roleId'
                        label='Role ID :'
                        value={formik.values.roleId.toString()}
                        className={FormStyles['role-name-input']}
                        disabled
                    />

                    <LabelledInput
                        id='roleName'
                        name='roleName'
                        label='Role Name* :'
                        value={formik.values.roleName}
                        error={formik.touched.roleName ? formik.errors.roleName : undefined}
                        onChange={(e) => {
                            if (error && setError) setError('');

                            formik.handleChange(e);
                        }}
                        onBlur={formik.handleBlur}
                        className={FormStyles['role-name-input']}
                        disabled={inputIsDisabled}
                    />

                    <LabelledInput
                        label='All Branches Access: '
                        onChange={handleAllBranchAccessChange}
                        type='checkbox'
                        checked={formik.values.allBranchAccess}
                        className={FormStyles.checkbox}
                        disabled={inputIsDisabled}
                    />

                    <LabelledDropdown
                        id='status'
                        name='status'
                        label='Status :'
                        options={statusOptions}
                        onSelectOption={handleOnSelectStatus}
                        value={formik.values.status}
                        error={formik.touched.status ? formik.errors.status : undefined}
                        onChange={handleStatusChange}
                        onBlur={formik.handleBlur}
                        className={FormStyles['role-name-input']}
                        disabled={inputIsDisabled}
                        autoComplete='off'
                        optionSelected={!!statusOptions.find(option => RoleStatusEnum[option.value] === formik.values.status)}
                    />
                </div>

                <RolePermissions
                    accessList={formik.values.access}
                    handleChange={formik.handleChange}
                    isDisabled={inputIsDisabled}
                    allBranchAccess={formik.values.allBranchAccess}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading || actionButtonIsDisabled && !formik.dirty}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default RolesRoleDetailsForm;
